import type { PublicPostPreview } from '@tomato/shared';
import { BOARD_LABELS, formatPrice, TALK_CATEGORY_LABELS } from '@tomato/shared';
import { useEffect, useState } from 'react';

import { Button, CenterCard, Logo } from '../components/ui';

/**
 * 공유 링크로 들어온 **로그아웃 방문자**용 프리뷰 화면.
 * 전체 본문·댓글·대화·다른 글은 가입해야 볼 수 있다(가입 유도).
 * 검색엔진 색인 방지를 위해 noindex 메타를 건다.
 */
export function PublicPostScreen({ postId }: { postId: string }) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [p, setP] = useState<PublicPostPreview | null>(null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/posts/${encodeURIComponent(postId)}`, {
      headers: { accept: 'application/json' },
    })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setStatus('notfound');
          return;
        }
        const data = (await res.json()) as { preview: PublicPostPreview };
        setP(data.preview);
        setStatus('ok');
      })
      .catch(() => !cancelled && setStatus('notfound'));
    return () => {
      cancelled = true;
    };
  }, [postId]);

  // 가입/로그인으로. ?ref=는 App이 이미 localStorage에 저장했으므로 홈으로 가면 로그인 화면.
  function goJoin() {
    window.location.href = '/';
  }

  if (status === 'loading') {
    return (
      <CenterCard>
        <Logo sub="사내 벼룩시장" />
        <p className="mt-6 text-center text-[13px] text-ink-400">불러오는 중…</p>
      </CenterCard>
    );
  }

  if (status === 'notfound' || !p) {
    return (
      <CenterCard>
        <Logo sub="사내 벼룩시장" />
        <p className="mt-6 text-center text-[14px] text-ink-600">이 글은 지금 볼 수 없어요.</p>
        <Button className="mt-5 w-full" onClick={goJoin}>
          토마토 마켓 둘러보기
        </Button>
      </CenterCard>
    );
  }

  const catLabel = p.category ? TALK_CATEGORY_LABELS[p.category] : null;
  const priceText = formatPrice(p.price, p.tradeType);

  return (
    <CenterCard>
      <Logo sub="사내 벼룩시장" />

      <div className="mt-5 overflow-hidden rounded-2xl bg-surface shadow-card">
        {p.thumbnailUrl && (
          <img src={p.thumbnailUrl} alt="" className="max-h-72 w-full object-cover" />
        )}
        <div className="p-4">
          <div className="flex items-center gap-1.5 text-[12px] text-ink-400">
            <span>{BOARD_LABELS[p.boardSlug]}</span>
            {catLabel && (
              <>
                <span aria-hidden>·</span>
                <span>{catLabel}</span>
              </>
            )}
          </div>
          <h1 className="mt-1 text-[18px] font-semibold text-ink-900">{p.title}</h1>
          {priceText && (
            <p className="mt-1 text-[15px] font-semibold text-ink-900">{priceText}</p>
          )}
          <p className="mt-1 text-[12px] text-ink-400">{p.author.nickname}</p>
          <p className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed text-ink-600">
            {p.excerpt}
            {p.hasMore && <span className="text-ink-300"> …</span>}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-tomato-50 p-4 text-center">
        <p className="text-[14px] font-medium text-tomato-700">
          전체 내용{p.commentCount > 0 ? ` · 댓글 ${p.commentCount}개` : ''}은 가입하면 볼 수 있어요
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-tomato-700/80">
          회사 메일(@hecto.co.kr)이면 누구나 바로 들어올 수 있어요. 서로 활동명으로만 오가고
          이메일은 아무에게도 보이지 않아요.
        </p>
        <Button className="mt-3 w-full" onClick={goJoin}>
          가입하고 전체 보기
        </Button>
      </div>
    </CenterCard>
  );
}
